export interface LocationMeasurement {
  latitude: number;
  longitude: number;
  accuracy: number;
  timestamp: number;
}

export interface FilteredLocation {
  latitude: number;
  longitude: number;
  accuracy: number;
  timestamp: number;
  velocity?: number;
  heading?: number;
}

export class KalmanFilter {
  private static instance: KalmanFilter;
  
  // State vector: [lat, lng, lat_velocity, lng_velocity]
  private state: number[] = [0, 0, 0, 0];
  
  // Covariance matrix
  private covariance: number[][] = [
    [1, 0, 0, 0],
    [0, 1, 0, 0],
    [0, 0, 1, 0],
    [0, 0, 0, 1]
  ];
  
  // Process noise covariance
  private Q: number[][] = [
    [0.1, 0, 0, 0],
    [0, 0.1, 0, 0],
    [0, 0, 0.1, 0],
    [0, 0, 0, 0.1]
  ];
  
  // Measurement noise covariance (will be updated based on accuracy)
  private R: number[][] = [
    [1, 0],
    [0, 1]
  ];
  
  // State transition matrix
  private F: number[][] = [
    [1, 0, 1, 0],
    [0, 1, 0, 1],
    [0, 0, 1, 0],
    [0, 0, 0, 1]
  ];
  
  // Measurement matrix
  private H: number[][] = [
    [1, 0, 0, 0],
    [0, 1, 0, 0]
  ];
  
  private lastTimestamp: number = 0;
  private isInitialized: boolean = false;

  private constructor() {}

  static getInstance(): KalmanFilter {
    if (!KalmanFilter.instance) {
      KalmanFilter.instance = new KalmanFilter();
    }
    return KalmanFilter.instance;
  }

  // Apply Kalman filter to location measurement
  applyFilter(measurement: LocationMeasurement): FilteredLocation {
    const currentTime = measurement.timestamp;
    
    if (!this.isInitialized) {
      this.initialize(measurement);
      return this.createFilteredLocation(measurement);
    }

    // Calculate time delta
    const dt = (currentTime - this.lastTimestamp) / 1000; // Convert to seconds
    
    if (dt <= 0) {
      return this.createFilteredLocation(measurement);
    }

    // Update state transition matrix with time delta
    this.updateStateTransitionMatrix(dt);
    
    // Update measurement noise based on accuracy
    this.updateMeasurementNoise(measurement.accuracy);
    
    // Predict step
    this.predict();
    
    // Update step
    this.update(measurement);
    
    this.lastTimestamp = currentTime;
    
    return this.createFilteredLocation(measurement);
  }

  // Initialize filter with first measurement
  private initialize(measurement: LocationMeasurement): void {
    this.state = [measurement.latitude, measurement.longitude, 0, 0];
    this.lastTimestamp = measurement.timestamp;
    this.isInitialized = true;
    
    // Initialize covariance with high uncertainty
    this.covariance = [
      [measurement.accuracy * measurement.accuracy, 0, 0, 0],
      [0, measurement.accuracy * measurement.accuracy, 0, 0],
      [0, 0, 100, 0],
      [0, 0, 0, 100]
    ];
  }

  // Update state transition matrix with time delta
  private updateStateTransitionMatrix(dt: number): void {
    this.F = [
      [1, 0, dt, 0],
      [0, 1, 0, dt],
      [0, 0, 1, 0],
      [0, 0, 0, 1]
    ];
  }

  // Update measurement noise based on accuracy
  private updateMeasurementNoise(accuracy: number): void {
    const accuracySquared = accuracy * accuracy;
    this.R = [
      [accuracySquared, 0],
      [0, accuracySquared]
    ];
  }

  // Predict step
  private predict(): void {
    // Predict state
    this.state = this.multiplyMatrixVector(this.F, this.state);
    
    // Predict covariance
    const F_transpose = this.transposeMatrix(this.F);
    const temp1 = this.multiplyMatrixMatrix(this.F, this.covariance);
    const temp2 = this.multiplyMatrixMatrix(temp1, F_transpose);
    this.covariance = this.addMatrix(temp2, this.Q);
  }

  // Update step
  private update(measurement: LocationMeasurement): void {
    // Measurement vector
    const z = [measurement.latitude, measurement.longitude];
    
    // Calculate Kalman gain
    const H_transpose = this.transposeMatrix(this.H);
    const temp1 = this.multiplyMatrixMatrix(this.H, this.covariance);
    const temp2 = this.multiplyMatrixMatrix(temp1, H_transpose);
    const S = this.addMatrix(temp2, this.R);
    const S_inverse = this.inverseMatrix(S);
    const temp3 = this.multiplyMatrixMatrix(this.covariance, H_transpose);
    const K = this.multiplyMatrixMatrix(temp3, S_inverse);
    
    // Update state
    const predicted_measurement = this.multiplyMatrixVector(this.H, this.state);
    const innovation = this.subtractVector(z, predicted_measurement);
    const correction = this.multiplyMatrixVector(K, innovation);
    this.state = this.addVector(this.state, correction);
    
    // Update covariance
    const I = this.identityMatrix(4);
    const temp4 = this.multiplyMatrixMatrix(K, this.H);
    const temp5 = this.subtractMatrix(I, temp4);
    this.covariance = this.multiplyMatrixMatrix(temp5, this.covariance);
  }

  // Create filtered location from current state
  private createFilteredLocation(measurement: LocationMeasurement): FilteredLocation {
    const velocity = Math.sqrt(this.state[2] * this.state[2] + this.state[3] * this.state[3]);
    const heading = Math.atan2(this.state[3], this.state[2]) * 180 / Math.PI;
    
    return {
      latitude: this.state[0],
      longitude: this.state[1],
      accuracy: measurement.accuracy,
      timestamp: measurement.timestamp,
      velocity: velocity,
      heading: heading,
    };
  }

  // Matrix multiplication helper
  private multiplyMatrixVector(a: number[][], b: number[]): number[] {
    const result: number[] = [];
    
    for (let i = 0; i < a.length; i++) {
      result[i] = 0;
      for (let j = 0; j < a[0].length; j++) {
        result[i] += a[i][j] * b[j];
      }
    }
    return result;
  }

  private multiplyMatrixMatrix(a: number[][], b: number[][]): number[][] {
    const result: number[][] = [];
    
    for (let i = 0; i < a.length; i++) {
      result[i] = [];
      for (let j = 0; j < b[0].length; j++) {
        result[i][j] = 0;
        for (let k = 0; k < a[0].length; k++) {
          result[i][j] += a[i][k] * b[k][j];
        }
      }
    }
    return result;
  }

  // Matrix transpose helper
  private transposeMatrix(matrix: number[][]): number[][] {
    const result: number[][] = [];
    for (let i = 0; i < matrix[0].length; i++) {
      result[i] = [];
      for (let j = 0; j < matrix.length; j++) {
        result[i][j] = matrix[j][i];
      }
    }
    return result;
  }

  // Matrix addition helper
  private addMatrix(a: number[][], b: number[][]): number[][] {
    const result: number[][] = [];
    for (let i = 0; i < a.length; i++) {
      result[i] = [];
      for (let j = 0; j < a[0].length; j++) {
        result[i][j] = a[i][j] + b[i][j];
      }
    }
    return result;
  }

  // Matrix subtraction helper
  private subtractMatrix(a: number[][], b: number[][]): number[][] {
    const result: number[][] = [];
    for (let i = 0; i < a.length; i++) {
      result[i] = [];
      for (let j = 0; j < a[0].length; j++) {
        result[i][j] = a[i][j] - b[i][j];
      }
    }
    return result;
  }

  // Vector addition helper
  private addVector(a: number[], b: number[]): number[] {
    const result: number[] = [];
    for (let i = 0; i < a.length; i++) {
      result[i] = a[i] + b[i];
    }
    return result;
  }

  // Vector subtraction helper
  private subtractVector(a: number[], b: number[]): number[] {
    const result: number[] = [];
    for (let i = 0; i < a.length; i++) {
      result[i] = a[i] - b[i];
    }
    return result;
  }

  // Identity matrix helper
  private identityMatrix(size: number): number[][] {
    const result: number[][] = [];
    for (let i = 0; i < size; i++) {
      result[i] = [];
      for (let j = 0; j < size; j++) {
        result[i][j] = i === j ? 1 : 0;
      }
    }
    return result;
  }

  // Matrix inverse helper (simplified for 2x2)
  private inverseMatrix(matrix: number[][]): number[][] {
    if (matrix.length === 2 && matrix[0].length === 2) {
      const det = matrix[0][0] * matrix[1][1] - matrix[0][1] * matrix[1][0];
      if (Math.abs(det) < 1e-10) {
        // Return identity if matrix is singular
        return this.identityMatrix(2);
      }
      const invDet = 1 / det;
      return [
        [matrix[1][1] * invDet, -matrix[0][1] * invDet],
        [-matrix[1][0] * invDet, matrix[0][0] * invDet]
      ];
    }
    // For larger matrices, return identity (simplified)
    return this.identityMatrix(matrix.length);
  }

  // Reset filter
  reset(): void {
    this.state = [0, 0, 0, 0];
    this.covariance = [
      [1, 0, 0, 0],
      [0, 1, 0, 0],
      [0, 0, 1, 0],
      [0, 0, 0, 1]
    ];
    this.lastTimestamp = 0;
    this.isInitialized = false;
  }

  // Get current state
  getState(): number[] {
    return [...this.state];
  }

  // Get current covariance
  getCovariance(): number[][] {
    return this.covariance.map(row => [...row]);
  }
}

export default KalmanFilter; 